import { Dispatch, SetStateAction } from 'react';
import { SummaryFeedbackType } from '@weave/schema-gen-ts/dist/schemas/fax/shared/v1/enums.pb';
import { UpdateSummaryFeedbackRequest } from '@weave/schema-gen-ts/dist/schemas/fax/summary/v1/fax_summary.pb';
import { FaxSummaryV1 } from '@frontend/api-fax-summary';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text, Button, useAlert } from '@frontend/design-system';
import { feedbackSectionStyles, feedbackButtonStyles } from './styles';

type FeedbackSectionProps = {
  feedbackSubmitSuccessCb: (feedbackType: SummaryFeedbackType) => void;
  faxSummaryId?: string;
  disableFeedback: boolean;
  locationId: string;
  openFeedbackModal: () => void;
  setFeedbackRequest: Dispatch<SetStateAction<UpdateSummaryFeedbackRequest | undefined>>;
};

export const FeedbackSection = ({
  faxSummaryId,
  locationId,
  feedbackSubmitSuccessCb,
  disableFeedback,
  openFeedbackModal,
  setFeedbackRequest,
}: FeedbackSectionProps) => {
  const { t } = useTranslation('fax');
  const alert = useAlert();

  const { mutate: submitFeedback, isLoading: isSubmittingFeedback } =
    FaxSummaryV1.Mutations.useUpdateSummaryFeedbackMutation({
      options: {
        onSuccess: (_, request) => {
          alert.success(t('Rating submitted. Thank you!'));
          feedbackSubmitSuccessCb(request.feedbackType);
          openFeedbackModal();
        },
        onError: () => {
          alert.error(t('Failed to submit rating. Please try again.'));
        },
      },
    });

  const handleFeedbackClick = (isHelpful: boolean) => {
    if (faxSummaryId) {
      const req = {
        id: faxSummaryId,
        locationId,
        feedbackType: isHelpful ? SummaryFeedbackType.POSITIVE : SummaryFeedbackType.NEGATIVE,
      };

      submitFeedback(req);
      setFeedbackRequest(req);
    } else {
      alert.error(t('Error submitting feedback. Please try again.'));
    }
  };

  return (
    <div css={feedbackSectionStyles}>
      <Text
        size='medium'
        color={disableFeedback ? 'disabled' : 'light'}
        css={{ marginLeft: theme.spacing(2), whiteSpace: 'nowrap' }}
      >
        {t('Is this helpful?')}
      </Text>
      <Button
        iconName='feedback-small'
        variant='secondary'
        size='small'
        css={feedbackButtonStyles}
        onClick={() => handleFeedbackClick(true)}
        label={t('Mark summary as helpful')}
        trackingId='fax-ai-summary-feedback-positive'
        disabled={isSubmittingFeedback || disableFeedback}
      />
      <Button
        iconName='feedback-bad-small'
        variant='secondary'
        size='small'
        css={feedbackButtonStyles}
        onClick={() => handleFeedbackClick(false)}
        label={t('Mark summary as not helpful')}
        trackingId='fax-ai-summary-feedback-negative'
        disabled={isSubmittingFeedback || disableFeedback}
      />
    </div>
  );
};
